import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FileText, UploadCloud, X } from 'lucide-react';
import styles from './FileUploader.module.scss';

/* ────────────────────────────────────────────────────────────
 * FileUploader — labelled document upload with a drop zone.
 *
 * The document-shaped sibling of ImagePicker: where ImagePicker
 * previews pixels in a square frame, FileUploader names a file in
 * a row, because a .pdf / .docx / .xlsx has nothing to show until
 * it is opened. Replaces the bare <Form.Control type="file"> plus
 * the hand-rolled filename string that recurred across My
 * Documents, Employee Documents, menu import/export, etc.
 *
 *   <FileUploader
 *     id="doc-file"
 *     value={file}                       // File | string name | null
 *     onChange={(file) => setFile(file)}
 *     onError={(msg) => setError(msg)}
 *     error={errorFromOutside}
 *     accept=".pdf, .jpg, .jpeg, .png, .xlsx, .xls"
 *     acceptedExtensions={['pdf', 'jpg', 'jpeg', 'png', 'xlsx', 'xls']}
 *     maxSizeBytes={10 * 1024 * 1024}
 *     helperText="PDF, image or spreadsheet, up to 10 MB."
 *   />
 *
 * Internal validation (size + extension) fires onError without
 * forwarding the file, so a caller never has to re-check what
 * `accept` already advertises. External validation (required,
 * server rejection) arrives through the `error` prop.
 *
 * Leave `label` unset when the uploader sits inside a
 * <ModalField>, which renders the label itself.
 * ──────────────────────────────────────────────────────────── */

const formatExtensions = (extensions) =>
  extensions.map((ext) => `.${ext.toLowerCase().replace(/^\./, '')}`);

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileUploader = ({
  id,
  label,
  value,
  onChange,
  onError,
  error,
  accept,
  acceptedExtensions,
  maxSizeBytes,
  helperText,
  required,
  disabled,
  className,
}) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // A File once the user picks one; a plain string when the caller
  // seeds the field with an already-stored document's name.
  const fileName = value instanceof File ? value.name : value || '';
  const fileSize = value instanceof File ? formatBytes(value.size) : '';
  const hasFile = Boolean(fileName);

  const acceptFile = useCallback(
    (file) => {
      if (!file) return;

      if (maxSizeBytes && file.size > maxSizeBytes) {
        const limitMb = Math.round(maxSizeBytes / (1024 * 1024));
        onError && onError(`File size should not exceed ${limitMb}MB.`);
        return;
      }

      if (acceptedExtensions && acceptedExtensions.length > 0) {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const allowed = acceptedExtensions.map((e) =>
          e.toLowerCase().replace(/^\./, '')
        );
        if (!allowed.includes(ext)) {
          const list = formatExtensions(acceptedExtensions).join(', ');
          onError && onError(`Only ${list} files are allowed.`);
          return;
        }
      }

      onChange(file);
    },
    [maxSizeBytes, acceptedExtensions, onChange, onError]
  );

  const handleInput = useCallback(
    (event) => {
      acceptFile(event.target.files && event.target.files[0]);
      // Clearing lets the same file be re-picked after a validation
      // failure — otherwise the second pick fires no change event.
      event.target.value = '';
    },
    [acceptFile]
  );

  const triggerPick = useCallback(() => {
    if (disabled) return;
    inputRef.current && inputRef.current.click();
  }, [disabled]);

  const clearFile = useCallback(() => {
    if (disabled) return;
    onChange(null);
  }, [disabled, onChange]);

  const handleDragOver = useCallback(
    (event) => {
      if (disabled) return;
      event.preventDefault();
      setIsDragging(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback(() => setIsDragging(false), []);

  const handleDrop = useCallback(
    (event) => {
      if (disabled) return;
      event.preventDefault();
      setIsDragging(false);
      acceptFile(event.dataTransfer.files && event.dataTransfer.files[0]);
    },
    [disabled, acceptFile]
  );

  const zoneClasses = [
    styles.zone,
    isDragging ? styles.zoneDragging : '',
    error ? styles.zoneError : '',
    disabled ? styles.zoneDisabled : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={[styles.wrapper, className || ''].filter(Boolean).join(' ')}
    >
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
          {required && (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      {hasFile ? (
        <div className={styles.file}>
          <span className={styles.fileGlyph} aria-hidden="true">
            <FileText size={18} strokeWidth={1.75} />
          </span>
          <span className={styles.fileText}>
            <span className={styles.fileName} title={fileName}>
              {fileName}
            </span>
            {fileSize && <span className={styles.fileMeta}>{fileSize}</span>}
          </span>
          <button
            type="button"
            className={styles.fileSwap}
            onClick={triggerPick}
            disabled={disabled}
          >
            Replace
          </button>
          <button
            type="button"
            className={styles.fileClear}
            onClick={clearFile}
            disabled={disabled}
            aria-label="Remove file"
          >
            <X size={15} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={zoneClasses}
          onClick={triggerPick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          disabled={disabled}
        >
          <span className={styles.zoneGlyph} aria-hidden="true">
            <UploadCloud size={22} strokeWidth={1.5} />
          </span>
          <span className={styles.zoneTitle}>
            Drop a file here, or <span className={styles.zoneLink}>browse</span>
          </span>
          {acceptedExtensions && acceptedExtensions.length > 0 && (
            <span className={styles.zoneMeta}>
              {formatExtensions(acceptedExtensions).join(' · ')}
            </span>
          )}
        </button>
      )}

      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        onChange={handleInput}
        disabled={disabled}
        className={styles.input}
      />

      {error ? (
        <span className={styles.error}>{error}</span>
      ) : helperText ? (
        <span className={styles.helperText}>{helperText}</span>
      ) : null}
    </div>
  );
};

FileUploader.propTypes = {
  id: PropTypes.string,
  label: PropTypes.node,
  // A File once picked, or a string filename when seeding edit mode.
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(typeof File !== 'undefined' ? File : Object),
  ]),
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
  error: PropTypes.string,
  accept: PropTypes.string,
  acceptedExtensions: PropTypes.arrayOf(PropTypes.string),
  maxSizeBytes: PropTypes.number,
  helperText: PropTypes.node,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

FileUploader.defaultProps = {
  id: undefined,
  label: null,
  value: null,
  onError: undefined,
  error: '',
  accept: undefined,
  acceptedExtensions: [],
  maxSizeBytes: 0,
  helperText: '',
  required: false,
  disabled: false,
  className: '',
};

export default FileUploader;
